<!--
FIXME: make payment need to be revisited to align with BE api and make sure on successful call, response includes referece data for payment successful page component
-->
<template>
  <div id="main-content">
    <div class="content-page-banner">
      <div class="banner-content">
        <h1>{{ !isCRM ? $route.meta.title : 'Make a payment' }}</h1>
        <div class="underline"></div>

        <div v-if="content !== null && content.topContent && !apiStatus.loading.refData && !apiStatus.fail.refData" v-html="content.topContent" />
      </div>
    </div>

    <ApiLoading v-if="apiStatus.loading.refData || apiStatus.loading.cardDetail" :apiError="false" />
    <Alert v-if="showSTTTokenExpiredMessage" alertType="error">
      <div v-if="content !== null && content.sessionTimeoutInformationText" v-html="content.sessionTimeoutInformationText" />
      <!-- this is hard coded error message as fall back in case ref data does not return message. -->
      <p v-else>Sorry, your session timed out. Please provide your payment details again to make a payment.</p>
    </Alert>
    <Alert v-if="datePaidToInvalid" alertType="error">
      <!-- datePaidToOver12MonthsText is not returned in the reference data API -->
      <div v-if="content !== null && content.datePaidToOver12MonthsText" v-html="content.datePaidToOver12MonthsText" />
    </Alert>
    <Alert
      v-if="this.apiStatus.fail.makePayment && !datePaidToInvalid"
      message="Something went wrong. We couldn’t confirm your payment information. Please try again."
      alertType="error"
    />
    <Alert v-else-if="Object.values(this.apiStatus.fail).some(Boolean) || paymentFailedDueToExpiredCard" alertType="error">
      <div v-if="paymentFailedDueToExpiredCard && content.expiredCardErrorMessage" v-html="content.expiredCardErrorMessage" />
    </Alert>

    <section id="multi-step-form" v-if="apiStatus.loading.refData === false && apiStatus.loading.cardDetail === false">
      <MultiStepFormNavigation :currentPage="currentStep" :links="sideMenu" @linkClicked="updateStep" :disabled="apiStatus.loading.makePayment || apiStatus.loading.ipsiToken" />

      <form id="form-contents" @submit.prevent novalidate>
        <!-- Review and submit -->
        <div v-if="currentStep === sideMenu[1].pages[0]" class="form-section review-and-submit">
          <ReviewAndSubmitComponent :infoKey="true" :updateKey="false">
            <h3 class="review-subheading">Payment Details</h3>
            <table class="review-and-submit-table">
              <tr>
                <th>Name on card</th>
                <td>{{ cardDetail.nameOnCard }}</td>
              </tr>
              <tr>
                <th>Card number</th>
                <td>{{ cardDetail.cardNumber }}</td>
              </tr>
              <tr>
                <th>CVV</th>
                <td>***</td>
              </tr>
              <tr>
                <th>Expiry</th>
                <td>{{ cardDetail.expiryDate }}</td>
              </tr>
              <tr class="info">
                <th>Payment amount</th>
                <td>
                  <strong>{{ formatAsCurrency(paymentAmount) }}</strong>
                </td>
              </tr>
            </table>
          </ReviewAndSubmitComponent>
        </div>

        <!-- step 1 -->
        <div v-else>
          <SectionSeparatorWrapper :headerText="sideMenu[currentStep - 1].linkTitle" :separator="false" headerRequiredFieldsText="indicates required field">
            <div class="form-section">
              <FormField
                labelText="Payment amount"
                placeholder="0.00"
                type="text"
                prefixLabel="$"
                v-model.lazy.trim="v$.paymentAmount.$model"
                @keydown="paymentAmountValueUpdate"
                @blur="formatPaymentAmount"
                :error="v$.paymentAmount.$error"
                :errorMessage="paymentAmountErrorMessage"
                :helperText="paymentAmountHelperText"
                :disabled="apiStatus.fail.maxPaymentAmount"
              >
                <template v-slot:helperText v-if="paymentAmountHelperText !== null">
                  <div v-html="paymentAmountHelperText" />
                </template>
              </FormField>
            </div>

            <div class="form-section" v-if="showHasNominatedCardOption && !apiStatus.fail.maxPaymentAmount">
              <FormField labelText="Would you like to use the credit card we have recorded?">
                <ul class="radio-group large">
                  <li v-for="(bool, idx) in [true, false]" :key="`haveNominated-${idx}`">
                    <input type="radio" name="hasNominatedCard" :id="`hasNominatedCard-${idx}-${bool}`" v-model="hasNominatedCard" :value="bool" @change="resetMemberInput()" />
                    <label :for="`hasNominatedCard-${idx}-${bool}`">{{ bool ? 'Yes' : 'No' }}</label>
                  </li>
                </ul>
              </FormField>
            </div>

            <div class="form-section card-detail" v-if="!apiStatus.fail.maxPaymentAmount">
              <FormField
                v-if="(hasNominatedCard !== null && showHasNominatedCardOption) || !showHasNominatedCardOption"
                labelText="Name on card"
                :helperText="showCreditCardForm && content !== null && !apiStatus.loading.ipsiToken && !apiStatus.fail.ipsiToken ? content.nameOnCardHelpText : null"
                :readOnly="hasNominatedCard"
                v-model.lazy="v$.cardDetail.nameOnCard.$model"
                :placeholder="hasNominatedCard === null || (hasNominatedCard !== null && hasNominatedCard === false) ? 'John Citizen' : null"
                :error="v$.cardDetail.nameOnCard.$error"
                :errorMessage="nameOnCardErrorMessage"
              />

              <FormField v-if="showCreditCardForm" :noLabelOverride="true">
                <template v-slot:helperText>
                  <div
                    v-if="content !== null && content.cardNumberHelpText && !apiStatus.loading.ipsiToken && !apiStatus.fail.ipsiToken"
                    v-html="content.cardNumberHelpText ? content.cardNumberHelpText : null"
                    class="credit-card-help-text"
                  />
                </template>
                <IpsiCreditCardForm
                  :immediatePaymentOnly="true"
                  :isCRM="isCRM"
                  @isCreditCardCompleted="updateCompletion"
                  @fetchCardToken="extractToken"
                  @fetchIpsiConfigCrmFail="
                    () => {
                      apiStatus.fail.ipsiToken = true;
                      apiStatus.fail.cardDetail = true;
                    }
                  "
                  @isApiLoading="handleIsApiLoading"
                  @isApiFailed="handleIsApiFailed"
                  @sttTokenTimeout="updateSttTokenTimeout"
                  ref="creditCardForm"
                />
              </FormField>

              <FormField v-if="showCreditCardForm">
                <p>
                  <b>Note:</b>
                  This card will not be stored on your account.
                </p>
              </FormField>

              <FormField v-if="apiStatus.loading.ipsiToken || apiStatus.fail.ipsiToken" overrideClass="bambora-loading">
                <ApiLoading :apiError="apiStatus.fail.ipsiToken" />
              </FormField>

              <div v-if="hasNominatedCard === true && showHasNominatedCardOption">
                <FormField readOnly labelText="Card number" v-model="v$.cardDetail.cardNumber.$model" />
                <FormField>
                  <div class="two-columns">
                    <div>
                      <FormLabel labelText="CVV" forId="cvv" />
                      <FormInput readOnly id="cvv" :modelValue="v$.cardDetail.cvv.$model" />
                    </div>

                    <div>
                      <FormLabel labelText="Expiry" forId="expiryDate" />
                      <FormInput readOnly id="expiryDate" :modelValue="v$.cardDetail.expiryDate.$model" />
                    </div>
                  </div>
                </FormField>
              </div>
            </div>
          </SectionSeparatorWrapper>
        </div>

        <!-- <FormField class="no-bottom-margin"> -->
        <div>
          <div v-if="isReviewAndSubmitStep" class="review-footer-text">
            <p v-if="!apiStatus.loading.makePayment">By submitting this payment, you are authorising Defence Health to make a charge to your nominated credit card.</p>
            <p v-if="!apiStatus.loading.makePayment">
              <strong>Privacy</strong>
            </p>
            <p v-if="!apiStatus.loading.makePayment">
              We have collected your credit card details in order that you may make a premium payment. Your information will be given to our service provider which arrange these
              payments. More information about how Defence Health manages your personal information is available in our Privacy Policy which is on our website. The Privacy Policy
              includes information about how you may access and correct your personal information and make a complaint.
            </p>
            <ApiLoading v-if="apiStatus.loading.makePayment" :apiError="false" />
          </div>

          <div class="form-page-nav" v-if="!apiStatus.loading.makePayment">
            <section v-if="isReviewAndSubmitStep">
              <button class="secondary" @click="updateStep(sideMenu[0].pages[0])">Back</button>
              <input :disabled="v$.$invalid" type="submit" value="Make a payment" @click="makePayment" />
            </section>

            <section v-else>
              <button class="secondary" @click="cancelPayment">Cancel</button>
              <button :disabled="v$.$invalid || apiStatus.loading.ipsiToken" @click="nextButtonClicked">Next</button>
            </section>
          </div>
        </div>
        <!-- </FormField> -->
      </form>
    </section>
  </div>
</template>

<script>
import cloneDeep from 'lodash.clonedeep';
import { mapGetters, mapMutations } from 'vuex';
import { required, requiredIf, maxLength } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { Alert, ApiLoading, FormField, FormInput, FormLabel, MultiStepFormNavigation, SectionSeparatorWrapper, ReviewAndSubmitComponent } from '@/components';
import { IpsiCreditCardForm } from '@general/js/components';
import { preventInvalidCurrencyInput, isValidCurrency, getCrmMemberNumber, isAcceptableAmount, getAccessLevel, formatAsCurrency, isBuildCRM, findPolicyHolder } from '@/utils';
import { getQueryString } from '@/utils/UrlUtils';
import { MY_COVER_PAYMENTS, MY_COVER_PAYMENTS_SUCCESSFUL } from '@/router/routeNames';
import { Payments as Services } from '@/services/MyCover';
import { VUEX_GENERAL, VUEX_MAKE_A_PAMENT } from '@/constants/VuexConstants';
import { MAX_PAYMENT_AMOUNT } from '@/constants';
import { AccessLevel } from '@/enums';
import { setNavigationState } from '@/router/navigationState';
import ErrorStrings from '@general/js/constants/ErrorStrings';
import { JOIN_NOW_NAME_VALIDATOR } from '@general/js/validators';
import { checkSomeObjectPropertiesTrue, getCurrentDate } from '@general/js/utils';
import { convertDateStringToText, reverseFormatFormDate } from '@general/js/utils/dateTimeUtils';
import { convertIpsiMaskedPanToCreditCardNumberDisplay } from '@/utils/DisplayUtils';

const isApplicationCRM = isBuildCRM();

const INITIAL_CARD_DETAIL = {
  nameOnCard: '',
  cardNumber: '',
  cvv: '',
  expiryDate: '',
  sttToken: '',
};

export default {
  name: 'MakeAPayment',

  components: {
    Alert,
    ApiLoading,
    IpsiCreditCardForm,
    FormField,
    FormInput,
    FormLabel,
    MultiStepFormNavigation,
    SectionSeparatorWrapper,
    ReviewAndSubmitComponent,
  },

  props: {
    CRMServices: {
      type: Function,
      default: null,
    },
    isFromCRM: {
      type: Boolean,
      default: false,
    },
    routeParams: {
      type: Object,
      // This is the component that should be shown upon clicking `Cancel` in make a payment form
      homePage: {
        type: String,
      },
    },
  },

  setup() {
    return { v$: useVuelidate() };
  },

  data() {
    return {
      apiStatus: {
        loading: {
          cardDetail: false,
          refData: true,
          makePayment: false,
          ipsiToken: false,
        },
        fail: {
          cardDetail: false,
          refData: false,
          makePayment: false,
          ipsiToken: false,
          maxPaymentAmount: false,
        },
      },
      paymentFailedDueToExpiredCard: false,
      content: null,
      isCreditCardCompleted: false,
      cardDetail: cloneDeep(INITIAL_CARD_DETAIL),
      sideMenu: [
        {
          linkTitle: 'Payment information',
          pages: [1],
          disabled: false,
          hidden: false,
        },
        {
          linkTitle: 'Review and submit',
          pages: [2],
          disabled: true,
          hidden: false,
        },
      ],
      sttToken: null,
      sttTimeoutTime: process.env.NODE_ENV === 'development' ? 10 * 1000 : 10 * 60 * 1000, // if in development, token expiry counter is 10 seconds. otherwise fallback to 10 mins
      showSTTTokenExpiredMessage: false,
      nominatedCardDetail: null,
      maxPaymentAmount: null,
      datePaidToInvalid: false,

      // CRM specific variables
      isCRM: false,
      crmMemberNumber: null,
      CRM_PAGES: {
        LANDING: 'Payments',
        MAKE_A_PAYMENT: 'MakeAPayment',
        RESULT: 'PaymentSuccessful',
      },
      crmTextData: {
        paymentSuccessfulInformationText: {
          informationText: '<p>Your Premium payment has been received.<br>Remember credit card payments will appear in your payment history by the next business day.</p>',
        },
        callOutHeading: {
          informationText: '<h2>This membership is covered until #{paidToDate}</h2>',
        },
        callOutText: {
          informationText: '<p>Don’t forget you can submit your claims online for fast easy payment.</p>',
        },
        callOutHeadingNoCoveredDate: {
          informationText: '<h2>Your payment has been received</h2>',
        },
        callOutTextNoCoveredDate: {
          informationText: '<p>We are currently unable to confirm the date your payment will provide cover up to for this membership. Please check back again shortly.</p>',
        },
        expiredCardErrorMessage: {
          informationText: '<p>Sorry, the expiry date on the credit card you entered appears invalid. Please review and update the card details.</p>',
        },
      },
      ErrorStrings,
    };
  },

  validations() {
    return {
      paymentAmount: {
        required,
        isValidCurrency,
        isAcceptableAmount(amount) {
          return isAcceptableAmount(amount, 1, this.maxPaymentAmount ?? MAX_PAYMENT_AMOUNT);
        },
      },
      hasNominatedCard: {
        required: requiredIf(() => this.showHasNominatedCardOption),
      },
      isCreditCardCompleted: {
        isCompleted(bool) {
          if (!this.showCreditCardForm) return true;
          return bool === true;
        },
      },
      cardDetail: {
        nameOnCard: {
          required,
          JOIN_NOW_NAME_VALIDATOR,
          maxLength: maxLength(50),
        },
        cardNumber: {
          required: requiredIf(() => !this.showCreditCardForm),
        },
        cvv: {
          required: requiredIf(() => !this.showCreditCardForm),
        },
        expiryDate: {
          required: requiredIf(() => !this.showCreditCardForm),
        },
      },
    };
  },

  computed: {
    ...mapGetters({
      memberships: VUEX_GENERAL.GETTERS.memberships,
      currentPolicyNumber: VUEX_GENERAL.GETTERS.getCurrentPolicyNumber,
      members: VUEX_GENERAL.GETTERS.getMembers,
      currentPersonCode: VUEX_GENERAL.GETTERS.getCurrentPersonCode,
    }),

    ...mapGetters('MakeAPayment', {
      currentStep: VUEX_MAKE_A_PAMENT.GETTERS.getCurrentStep,
      getPaymentAmount: VUEX_MAKE_A_PAMENT.GETTERS.getPaymentAmount,
      getHasNominatedCard: VUEX_MAKE_A_PAMENT.GETTERS.getHasNominatedCard,
    }),

    showGeneralApiFailMessage() {
      const failType = this.apiStatus.fail;
      return [failType.ipsiToken, failType.cardDetail, failType.refData].some(Boolean);
    },

    paymentAmount: {
      get() {
        return this.getPaymentAmount;
      },
      set(data) {
        this.updatePaymentAmount(data);
      },
    },

    hasNominatedCard: {
      get() {
        return this.getHasNominatedCard;
      },
      set(data) {
        this.updateHasNominatedCard(data);
      },
    },

    showCreditCardForm() {
      return (this.hasNominatedCard === false && this.showHasNominatedCardOption) || !this.showHasNominatedCardOption;
    },

    isReviewAndSubmitStep() {
      return this.currentStep === this.sideMenu[this.sideMenu.length - 1].pages[0];
    },

    isSelfAccess() {
      // As for CRM, there is no need to check it is self access or not, so it will always return false
      if (this.isFromCRM) return false;

      const thisAccessLevel = getAccessLevel(this.currentPolicyNumber, this.memberships);

      return thisAccessLevel.toLowerCase() === AccessLevel.SELF_ACCESS;
    },

    isPolicyHolder() {
      const thisAccessLevel = getAccessLevel(this.currentPolicyNumber, this.memberships);

      return thisAccessLevel.toLowerCase() === AccessLevel.POLICY_HOLDER_ACCESS;
    },

    showHasNominatedCardOption() {
      if (this.isSelfAccess) {
        return false;
      }

      // Checking for keep private flag if not policy holder and not CRM
      if (!this.isCRM && !this.isPolicyHolder) {
        const policyHolder = findPolicyHolder(this.members);
        if (policyHolder?.keepPrivateflag) return false;
      }

      return this.nominatedCardDetail !== null && !this.apiStatus.loading.cardDetail && !this.apiStatus.fail.cardDetail;
    },

    paymentAmountErrorMessage() {
      const v = this.v$.paymentAmount;
      let msg = '';

      if (v.isAcceptableAmount.$invalid) {
        // Change validation error for DE2E-7662
        // msg = ErrorStrings.CURRENCY_INPUT.NOT_ACCEPTABLE_AMOUNT(1, this.maxPaymentAmount ?? MAX_PAYMENT_AMOUNT);
        msg = ErrorStrings.CURRENCY_INPUT.NOT_ACCEPTABLE_AMT_WITHOUT_MAX_AMT;
      }

      if (v.isValidCurrency.$invalid) {
        msg = ErrorStrings.CURRENCY_INPUT.NOT_VALID_CURRENCY;
      }

      return msg;
    },

    paymentAmountHelperText() {
      if (this.content !== null && this.content?.paymentAmountHelpText) {
        const FALLBACK_MAX_PAYMENT_AMOUNT = `$${Number(MAX_PAYMENT_AMOUNT).toLocaleString()}`;
        const tokenSwapWith = this.maxPaymentAmount !== null ? `$${Number(Number(this.maxPaymentAmount).toFixed(2)).toLocaleString()}` : FALLBACK_MAX_PAYMENT_AMOUNT;
        const helpTextInString = JSON.stringify(this.content.paymentAmountHelpText).replace(/#{maximumPaymentAmount}/g, tokenSwapWith);

        return JSON.parse(helpTextInString);
      }

      return null;
    },

    nameOnCardErrorMessage() {
      const v = this.v$.cardDetail.nameOnCard;

      if (v.required.$invalid) {
        return ErrorStrings.NAME_ON_CARD.GENERIC_ERROR;
      }

      if (v.maxLength.$invalid) {
        return ErrorStrings.NAME_ON_CARD.MAX_LENGTH;
      }

      return ErrorStrings.NAME;
    },
  },

  watch: {
    'v$.$invalid': {
      handler(bool) {
        this.sideMenu[1].disabled = bool;
      },
    },

    hasNominatedCard(bool) {
      this.isCreditCardCompleted = false;
      this.showSTTTokenExpiredMessage = false;
      this.sttToken = null;

      if (bool === false) {
        // reset user input
        this.cardDetail = cloneDeep(INITIAL_CARD_DETAIL);
      } else {
        const nominatedCardDetail = cloneDeep(this.nominatedCardDetail);
        this.cardDetail = nominatedCardDetail;
        this.sttToken = nominatedCardDetail.sttToken;
      }

      // Reset API loading & Fail states
      this.apiStatus.loading.ipsiToken = false;
      this.apiStatus.fail.ipsiToken = false;
    },
  },

  beforeMount() {
    const currentStep = Number(getQueryString('step'));

    if (Number.isNaN(currentStep) || currentStep <= 0) {
      this.updateStep(this.sideMenu[0].pages[0]);
    } else if ((this.hasNominatedCard === false || this.showHasNominatedCardOption === false) && currentStep === this.sideMenu[this.sideMenu.length - 1].pages[0]) {
      this.updateStep(this.sideMenu[0].pages[0]);
      this.showSTTTokenExpiredMessage = true;
    } else {
      this.updateStep(currentStep);
    }

    this.isCRM = this.isFromCRM ? this.isFromCRM : isApplicationCRM;
  },

  mounted() {
    if (!this.isCRM && this.$attrs?.paymentAmount) {
      this.updatePaymentAmount(Number(this.$attrs.paymentAmount).toFixed(2));
    }

    if (this.isCRM) {
      if (this.routeParams && this.routeParams.memberNumber) {
        this.crmMemberNumber = this.routeParams.memberNumber;
      } else {
        this.crmMemberNumber = getCrmMemberNumber();
      }
      if (this.routeParams && this.routeParams.paymentAmount) {
        this.updatePaymentAmount(Number(this.routeParams.paymentAmount).toFixed(2));
      } else {
        this.updateCRMPremiumCalculatorPaymentAmount();
      }
    }

    if (this.getPaymentAmount !== '') {
      // if there is a payment amount from premium calc, ensure validation messages are presented if necessary
      this.v$.paymentAmount.$touch();
    }
    this.datePaidToInvalid = false;

    this.getReferenceData();
  },

  beforeUnmount() {
    this.resetComponent();
  },

  methods: {
    ...mapMutations('MakeAPayment', {
      resetComponent: VUEX_MAKE_A_PAMENT.MUTATIONS.resetStore,
      updatePaymentAmount: VUEX_MAKE_A_PAMENT.MUTATIONS.updatePaymentAmount,
      updateCurrentStep: VUEX_MAKE_A_PAMENT.MUTATIONS.updateCurrentStep,
      updateHasNominatedCard: VUEX_MAKE_A_PAMENT.MUTATIONS.updateHasNominatedCard,
    }),

    /**
     * emit data to parent for page navigation in CRM.
     *
     * @param {string} targetPageName name of target page. it should be one of `PAGES` object value.
     * @param {object} routeParams by default is empty object. if given, it should be object. this object will be used like route param in non CRM build.
     */
    crmNavigatePage(targetPageName, routeParams = {}) {
      window.scrollTo(0, 0);
      this.$emit('crmNavigatePage', targetPageName, routeParams);
    },

    updateCRMPremiumCalculatorPaymentAmount() {
      if (window.parent.getContext) {
        // if route is coming from premium calculator in CRM, fill the payment amount
        if (this.getCRMUrlParameters().data) {
          const context = window.parent.getContext;
          let amount;
          // gets the crm value for payment amount from premium calculator
          if (context.getAttribute('dhl_calculate')) {
            if (context.getAttribute('dhl_calculate').getValue() === 2) {
              // this amount is the amount given by the premium calculator when date is entered
              amount = context.getAttribute('dhl_paymentamounttext') ? `${context.getAttribute('dhl_paymentamounttext').getValue()}`.replace('$', '') : null;
            } else {
              // this amount is the amount entered into the premium calculator if amount is entered
              const setAmount = context.getAttribute('dhl_setamount') ? context.getAttribute('dhl_setamount').getValue() : null;
              amount = `${JSON.parse(setAmount).initialPayment}`.replace('$', '');
            }
          }
          if (amount !== null || amount !== undefined) {
            this.updatePaymentAmount(Number(amount).toFixed(2));
          }
        }
      } else {
        this.updatePaymentAmount('');
      }
    },

    handleIsApiLoading(bool) {
      this.apiStatus.loading.ipsiToken = bool;
    },

    handleIsApiFailed(bool) {
      this.apiStatus.fail.ipsiToken = bool;
    },

    nextButtonClicked() {
      this.updateStep(this.sideMenu[1].pages[0]);
    },

    resetBamboraForm() {
      if (!this.hasNominatedCard) {
        this.isCreditCardCompleted = false;
        this.sttToken = null;
        const currentNameOnCard = this.cardDetail.nameOnCard;
        this.cardDetail = cloneDeep(INITIAL_CARD_DETAIL);
        this.cardDetail.nameOnCard = currentNameOnCard;
      }
    },

    updateSttTokenTimeout(timeout) {
      this.sttTimeoutTime = timeout * 60 * 1000; // convert to milliseconds
    },

    async submitPaymentForm() {
      try {
        this.apiStatus.loading.ipsiToken = true;
        this.apiStatus.fail.ipsiToken = false;

        await this.$refs.creditCardForm.submitAwait();
      } catch (error) {
        this.apiStatus.fail.ipsiToken = true;
        logger.log('Error submitting Ipsi iFrame', error);
      } finally {
        this.apiStatus.loading.ipsiToken = false;
      }
    },

    updateCompletion(data) {
      this.isCreditCardCompleted = data;

      if (data === false) {
        this.resetBamboraForm();
      }
    },

    /**
     * Event handler that is automatically triggered when a Secure Transaction Token (STT) is successfully retrieved from IPSI
     */
    extractToken(data) {
      if (data.sttLoadStatus === true) {
        this.showSTTTokenExpiredMessage = false;
        this.sttToken = data.token;
        this.cardDetail.cardNumber = convertIpsiMaskedPanToCreditCardNumberDisplay(data.maskedPAN);
        this.cardDetail.cvv = '***';
        this.cardDetail.expiryDate = `${data.cardExpiryDate.split('/')[0]} / ${data.cardExpiryDate.split('/')[1]}`;
        this.sttTimer = setTimeout(() => {
          this.resetToken();
        }, this.sttTimeoutTime);
      } else {
        this.apiStatus.fail.ipsiToken = true;
      }
    },

    resetToken() {
      this.sttToken = null;
      logger.log('stt token expired');
    },

    cancelPayment() {
      if (this.routeParams?.homePage) {
        this.navigateToOtherPage(this.routeParams.homePage);
      } else {
        this.navigateToOtherPage(MY_COVER_PAYMENTS);
      }
    },

    async updateStep(stepNumber) {
      if (stepNumber === this.sideMenu[0].pages[0]) {
        this.paymentFailedDueToExpiredCard = false;
        this.apiStatus.fail.makePayment = false;
        this.resetBamboraForm();
        this.resetMemberInput();
      }

      if (this.currentStep === this.sideMenu[0].pages[0]) {
        if (this.showCreditCardForm && this.v$.$invalid === false) {
          // Fetch token if moving from step 1 to 2 and form is valid and credit card form is shown
          await this.submitPaymentForm();
        }
      }

      this.updateCurrentStep(stepNumber);
      window.scrollTo(0, 0); // scroll to top
    },

    // `to` should be one of const value from @/router/routeNames.js
    navigateToOtherPage(to, param = {}) {
      setNavigationState(param);
      this.$router.push({
        name: to,
        params: {
          policyNumber: this.$route.params.policyNumber,
        },
      });
    },

    async makePayment() {
      // if new card and token is expired, return to first step and ask member to refill ipsi credit card form
      if (this.isCreditCardCompleted && this.sttToken === null) {
        this.showSTTTokenExpiredMessage = true;
        this.resetBamboraForm();
        this.updateStep(this.sideMenu[0].pages[0]);
      } else {
        this.apiStatus.fail.makePayment = false;
        this.apiStatus.loading.makePayment = true;

        try {
          if (this.isCRM) {
            const requestPayload = {
              token: this.sttToken,
              amount: Number(this.paymentAmount),
              type: this.hasNominatedCard ? 'card' : 'stt',
              currentDate: getCurrentDate(),
              cardHolderName: this.cardDetail.nameOnCard,
              memberNumber: this.crmMemberNumber,
            };

            let services = Services;
            services = this.CRMServices ? this.CRMServices : Services;
            const res = await services.makePayment(requestPayload);

            let isCoveredDateAvailable = false;
            let datePaidTo;
            if (res.newDatePaidTo || res.originalDatePaidTo) {
              isCoveredDateAvailable = true;
              if (res.newDatePaidTo) {
                datePaidTo = res.newDatePaidTo;
              } else {
                datePaidTo = res.originalDatePaidTo;
              }
            } else {
              datePaidTo = '';
            }

            // sets the call out heading and text for the PaymentSuccessful page
            const callOutHeading = isCoveredDateAvailable
              ? this.crmTextData.callOutHeading.informationText.replace(/#{paidToDate}/i, convertDateStringToText(datePaidTo))
              : this.crmTextData.callOutHeadingNoCoveredDate.informationText;
            const callOutText = isCoveredDateAvailable ? this.crmTextData.callOutText.informationText : this.crmTextData.callOutTextNoCoveredDate.informationText;

            const paramsForSuccessPage = {
              content: {
                paymentSuccessfulInformationText: this.crmTextData.paymentSuccessfulInformationText.informationText,
                callOutHeading,
                callOutText,
                relatedArticles: [res?.relatedArticle1, res?.relatedArticle2, res?.relatedArticle3],
              },
              premiumAmountPaid: formatAsCurrency(requestPayload.amount),
              surcharge: formatAsCurrency(res.amountPaid / 100 - requestPayload.amount),
              referenceNumber: res.paymentReference,
            };

            this.crmNavigatePage(this.CRM_PAGES.RESULT, paramsForSuccessPage);
            this.resetComponent();
          } else if (!this.isCRM) {
            const requestPayload = {
              currentDate: getCurrentDate(),
              amount: Number(this.paymentAmount),
              type: this.hasNominatedCard ? 'card' : 'stt',
              token: this.sttToken,
              cardHolderName: this.cardDetail.nameOnCard,
            };

            const res = await Services.makePayment(this.currentPolicyNumber, requestPayload);
            const paymentSuccessRouteParams = await this.formatPaymentSuccessRefData(res, false, requestPayload);

            this.navigateToOtherPage(MY_COVER_PAYMENTS_SUCCESSFUL, paymentSuccessRouteParams);
            this.resetComponent();
          }
        } catch (err) {
          this.apiStatus.fail.makePayment = true;
          if (!this.isCRM) {
            // Get error data from API call
            const errorObj = JSON.parse(JSON.stringify(err));

            // If payment failed after payment was processed navigate to payment success page
            if (errorObj.response?.data?.step === 'POST-PAYMENT') {
              const transactionData = errorObj.response.data;
              const requestPayload = {
                currentDate: getCurrentDate(),
                amount: Number(this.paymentAmount),
                type: this.hasNominatedCard ? 'card' : 'stt',
                token: this.sttToken,
                cardHolderName: this.cardDetail?.nameOnCard,
              };
              const paymentSuccessRouteParams = await this.formatPaymentSuccessRefData(transactionData, true, requestPayload);
              this.navigateToOtherPage(MY_COVER_PAYMENTS_SUCCESSFUL, paymentSuccessRouteParams);
              this.resetComponent();
            }
          }
          logger.log(err);
        } finally {
          this.apiStatus.loading.makePayment = false;
        }
      }
    },

    async getReferenceData() {
      this.apiStatus.fail.refData = false;
      this.apiStatus.loading.refData = true;
      try {
        if (!this.isCRM) {
          const res = await Services.getMakeAPaymentReferenceData();
          this.content = {
            topContent: this.isSelfAccess ? res?.makeAPaymentSelfAccessInformationText?.informationText : res?.makeAPaymentFullAccessInformationText?.informationText,
            nameOnCardHelpText: this.isSelfAccess ? res?.newCardHelpText?.informationText : res?.nominatedCardHelpText.informationText,
            cardNumberHelpText: res?.cardNumberHelpText?.informationText,
            sessionTimeoutInformationText: res?.sessionTimeoutInformationText?.informationText,
            reviewMessage: res?.makeAPaymentConfirmationText?.informationText,
            paymentAmountHelpText: res?.paymentAmountInformationText?.informationText,
            datePaidToOver12MonthsText: res?.datePaidToOver12MonthsInformationText?.informationText,
            expiredCardErrorMessage: res?.expiredCardErrorMessage?.informationText,
          };

          try {
            const maxAmount = await Services.getMakeAPaymentMaximumAmount(this.currentPolicyNumber);
            this.maxPaymentAmount = Number(maxAmount).toFixed(2);
          } catch (err) {
            logger.error(err);
            this.apiStatus.fail.maxPaymentAmount = true;
            this.maxPaymentAmount = null;
            this.content.paymentAmountHelpText = this.content.paymentAmountHelpText.replace('The maximum payment you can make today is #{maximumPaymentAmount}.', '');
          }
        } else {
          const services = this.CRMServices ? this.CRMServices : Services;
          this.content = {
            expiredCardErrorMessage: this.crmTextData.expiredCardErrorMessage.informationText,
          };

          try {
            const maxAmount = await services.getMakeAPaymentMaximumAmount(this.crmMemberNumber);
            const amountPayment = JSON.parse(maxAmount);
            this.maxPaymentAmount = Number(amountPayment.initialPayment).toFixed(2);
          } catch (err) {
            this.maxPaymentAmount = Number('20500').toFixed(2);
          }
        }
      } catch (err) {
        this.apiStatus.fail.refData = true;
        logger.error(err);
      } finally {
        this.apiStatus.loading.refData = false;
      }

      if (!this.isSelfAccess) {
        this.getNominatedCardDetail();
      }
    },

    async getPaymentSuccessReferenceData() {
      if (!this.isCRM) {
        this.apiStatus.fail.refData = false;
        this.apiStatus.loading.refData = true;

        try {
          const paymentSuccessRefData = await Services.getPaymentSuccessReferenceData();

          return paymentSuccessRefData;
        } catch (err) {
          this.apiStatus.fail.refData = true;
          logger.error(err);
        } finally {
          this.apiStatus.loading.refData = false;
        }
      }

      return '';
    },

    async getNominatedCardDetail() {
      const loadedStep = Number(getQueryString('step'));
      this.apiStatus.fail.cardDetail = false;
      this.apiStatus.loading.cardDetail = true;
      this.nominatedCardDetail = null;

      if (!this.isCRM) {
        try {
          const res = await Services.getCardDetail(this.currentPolicyNumber);

          let expiryDate = res.expiryDate.split('-');
          expiryDate = `${expiryDate[1]} / ${expiryDate[0].slice(2)}`;

          const nominatedCardDetail = {
            nameOnCard: res.accountHolderName,
            cardNumber: this.formatCreditCardNumber(res.maskedCreditCardNumber),
            cvv: '***',
            expiryDate,
            sttToken: res.accountNumberOrToken,
          };

          if (this.hasNominatedCard === true) {
            this.cardDetail = cloneDeep(nominatedCardDetail);
            this.sttToken = res.accountNumberOrToken;
          }

          this.nominatedCardDetail = nominatedCardDetail;
        } catch (err) {
          // if member doesn't have a nominated card do not fail API call
          if (this.hasNominatedCard) {
            this.apiStatus.fail.cardDetail = true;

            if (loadedStep === this.sideMenu[1].pages[0] && this.hasNominatedCard === true) {
              this.updateStep(this.sideMenu[0].pages[0]);
            }
            logger.error(err);
          }
        } finally {
          this.apiStatus.loading.cardDetail = false;
        }
      } else if (window.parent.getContext) {
        // if in crm, get attributes from Form Context
        const context = window.parent.getContext;
        const expiryYear = context.getAttribute('dhl_expyear') ? `${context.getAttribute('dhl_expyear').getValue()}` : null;
        const nominatedCardDetail = {
          nameOnCard: context.getAttribute('dhl_nameoncard') ? context.getAttribute('dhl_nameoncard').getValue() : null,
          cardNumber: context.getAttribute('dhl_futurepaymentcardnumber') ? this.formatCreditCardNumber(`${context.getAttribute('dhl_futurepaymentcardnumber').getValue()}`) : null,
          cvv: '***',
          expiryDate: context.getAttribute('dhl_expmonth') ? `${context.getAttribute('dhl_expmonth').getValue()} / ${expiryYear.slice(-2)}` : null,
          sttToken: context.getAttribute('dhl_bamboratoken') ? context.getAttribute('dhl_bamboratoken').getValue() : null,
        };
        // if any details are null, there is no credit card details
        if (
          Object.values(nominatedCardDetail).some((cardDetails) => {
            return cardDetails === null;
          })
        ) {
          this.nominatedCardDetail = null;
        } else {
          this.nominatedCardDetail = nominatedCardDetail;
        }

        if (this.hasNominatedCard) {
          this.cardDetail = cloneDeep(nominatedCardDetail);
          this.sttToken = context.getAttribute('dhl_bamboratoken') ? context.getAttribute('dhl_bamboratoken').getValue() : null;
        }
        this.apiStatus.loading.cardDetail = false;
      } else {
        this.apiStatus.loading.cardDetail = false;
      }
    },

    paymentAmountValueUpdate(event) {
      if (preventInvalidCurrencyInput(event)) {
        this.updatePaymentAmount(event.target.value);
      } else {
        event.preventDefault();
      }
    },

    formatPaymentAmount(event) {
      this.updatePaymentAmount(Number(event.target.value).toFixed(2));
    },

    /**
     * Formats credit card number to space between every 4 digits
     * E.g. '400054663022' to '4000 5466 3022'
     * @param {string} creditCardNumber - card number in string format.
     */
    formatCreditCardNumber(creditCardNumber) {
      return creditCardNumber.match(/.{1,4}/g).join(' ');
    },

    /**
     * Finds the url parameters from CRM
     * Checks if the 'Make a Payment' page is accessed through Premium Calculator
     * @returns the dhl_membership policy id from url params on CRM or empty object if no parameter is found
     */
    getCRMUrlParameters() {
      const queryString = window.location.search.substring(1);
      const params = {};
      const queryStringParts = queryString.split('&');
      for (let i = 0; i < queryStringParts.length; i++) {
        const pieces = queryStringParts[i].split('=');
        params[pieces[0].toLowerCase()] = pieces.length === 1 ? null : decodeURIComponent(pieces[1]);
      }

      return params;
    },

    /**
     * Determines which data should be shown in the payment success callout based on which values are returned by the API response
     * Returns empty string if these values are not received
     *
     * @returns {String} DPT
     */
    calcDatePaidTo(newDatePaidTo, originalDatePaidTo) {
      let datePaidTo;
      if (newDatePaidTo || originalDatePaidTo) {
        if (newDatePaidTo) {
          datePaidTo = newDatePaidTo;
        } else {
          datePaidTo = originalDatePaidTo;
        }
      } else {
        datePaidTo = '';
      }
      return datePaidTo;
    },

    /**
     * Prepares the content for payment success view
     *
     * @param {Object} transactionData - the result of the makePayment API call
     * @param {Boolean} isError - whether this functions is being called from the error handling part of the try/catch block
     *
     * @returns {Object} payment success content
     */
    async formatPaymentSuccessRefData(transactionData, isError, paymentPayLoad) {
      const paymentSuccessReferenceData = await this.getPaymentSuccessReferenceData();
      const datePaidTo = this.calcDatePaidTo(transactionData.newDatePaidTo, transactionData.originalDatePaidTo);

      // If !isError the callout section is required to show the DPT
      let calloutContent = '';
      if (!isError) {
        calloutContent = paymentSuccessReferenceData.paymentSuccessDatePaidToCalloutText.informationText.replace('#{DPT}', convertDateStringToText(datePaidTo));
      }

      return {
        refData: {
          content: {
            paymentSuccessfulInformationText: paymentSuccessReferenceData.paymentSuccessInformationText.informationText,
            callOutText: isError ? paymentSuccessReferenceData.paymentSuccessDatePaidToErrorCalloutText.informationText : calloutContent,
            paymentSuccessCalloutIcon: paymentSuccessReferenceData.paymentSuccessCalloutIcon.informationText,
            relatedPages: [paymentSuccessReferenceData.relatedPage1, paymentSuccessReferenceData.relatedPage2, paymentSuccessReferenceData.relatedPage3],
          },
          premiumAmountPaid: formatAsCurrency(paymentPayLoad.amount),
          referenceNumber: transactionData.paymentReference,
          nextPaymentDate: reverseFormatFormDate(transactionData.contributionDates.current),
        },
      };
    },

    /**
     * resets the validation on the form, so that when members switch between nominated CC errors messages are not triggered
     */
    resetMemberInput() {
      if (!this.hasNominatedCard) {
        this.v$.$reset();
      }
    },

    formatAsCurrency,
    checkSomeObjectPropertiesTrue,
    isBuildCRM,
  },
};
</script>

<style lang="scss" scoped>
.top-content {
  margin-bottom: spacer(5);
}

.two-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: rem(20) rem(15);

  @include mobile {
    grid-template-columns: 1fr;
  }
}

.card-detail {
  position: relative;

  .bambora-loading {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    :deep(.api-loading-wrapper) {
      min-height: 0;
      height: 100%;
      box-sizing: border-box;
    }
  }
}

:deep(.prefixed-input) {
  @include flex-row;
  @include flex-v-center;
  background-color: $PRIMARY_GREY_20;
  line-height: 3;
  label {
    width: auto;
    height: 100%;
    margin-right: 12px;
    margin-left: 12px;
    text-align: center;
  }
  input {
    margin-bottom: 0;
  }
}

:deep(.wrapper-with-prefix) {
  display: flex;
  align-items: center;
  border-radius: 6px;
  border: 1.5px solid $PRIMARY_GREY_40;
  background-color: $PRIMARY_GREY_20;
  input {
    border: none !important;

    &:focus {
      margin-bottom: unset !important;
    }
  }
}

:deep(.credit-card-help-text) {
  margin-top: 35px;

  @include mobile {
    margin-top: 0;
  }
}
</style>
